import React, { useState, useRef } from 'react';
import { X, FileSpreadsheet, UploadCloud, Download, CheckCircle2, AlertCircle, Loader2, Trash2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { Button } from '../ui/Button';
import { QuestionOption, QuestionSet } from '../../types';
import { getStoredToken, getAuthHeaders, customFetch } from '../../services/apiService';

interface ExcelImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetSet: QuestionSet;
  onSuccess: (addedCount: number) => void;
  onQuotaExceeded?: (message?: string) => void;
}

export const ExcelImportModal: React.FC<ExcelImportModalProps> = ({
  isOpen,
  onClose,
  targetSet,
  onSuccess,
  onQuotaExceeded
}) => {
  const [parsedQuestions, setParsedQuestions] = useState<Array<{
    questionText: string;
    options: QuestionOption[];
    explanation: string;
    difficulty: string;
  }>>([]);
  const [selectedRawFile, setSelectedRawFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        'Câu hỏi': 'Thủ đô của nước Cộng hòa Xã hội Chủ nghĩa Việt Nam là gì?',
        'Đường dẫn ảnh': 'https://example.com/image.jpg',
        'Đáp án A': 'Thành phố Hồ Chí Minh',
        'Đáp án B': 'Hà Nội',
        'Đáp án C': 'Đà Nẵng',
        'Đáp án D': 'Hải Phòng',
        'Đáp án đúng': 'B',
        'Giải thích': 'Hà Nội là thủ đô của nước CHXHCN Việt Nam.',
        'Mức độ': 'Nhận biết'
      },
      {
        'Câu hỏi': 'Cho hàm số y = ax² + bx + c (a ≠ 0). Đỉnh của parabol có tọa độ là:',
        'Đường dẫn ảnh': '',
        'Đáp án A': 'I(-b/2a; -Δ/4a)',
        'Đáp án B': 'I(-b/a; -Δ/4a)',
        'Đáp án C': 'I(b/2a; Δ/4a)',
        'Đáp án D': 'I(-b/2a; -Δ/2a)',
        'Đáp án đúng': 'A',
        'Giải thích': 'Tọa độ đỉnh I của parabol là (-b/2a; -Δ/4a).',
        'Mức độ': 'Thông hiểu'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Mau_Cau_Hoi');
    XLSX.writeFile(wb, 'Mau_Import_Cau_Hoi_NhaGiaoSo.xlsx');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedRawFile(file);
    setLoading(true);
    setError(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsName = wb.SheetNames[0];
        const ws = wb.Sheets[wsName];
        const rows: any[] = XLSX.utils.sheet_to_json(ws);

        if (!rows || rows.length === 0) {
          throw new Error('File Excel không có dữ liệu hoặc không đúng định dạng.');
        }

        const formattedList = rows.map((row) => {
          // Normalize column keys
          const qText = row['Câu hỏi'] || row['Cau hoi'] || row['Question'] || row['Noi dung'] || '';
          const qMedia = row['Đường dẫn ảnh'] || row['Duong dan anh'] || row['Ảnh'] || row['Media'] || row['Image'] || row['ImageUrl'] || '';
          const optA = row['Đáp án A'] || row['Dap an A'] || row['Option A'] || row['A'] || '';
          const optB = row['Đáp án B'] || row['Dap an B'] || row['Option B'] || row['B'] || '';
          const optC = row['Đáp án C'] || row['Dap an C'] || row['Option C'] || row['C'] || '';
          const optD = row['Đáp án D'] || row['Dap an D'] || row['Option D'] || row['D'] || '';
          const correctKey = String(row['Đáp án đúng'] || row['Dap an dung'] || row['Correct'] || 'A').trim().toUpperCase();
          const expl = row['Giải thích'] || row['Giai thich'] || row['Explanation'] || '';
          const diff = row['Mức độ'] || row['Muc do'] || row['Difficulty'] || 'Thông hiểu';

          const options: QuestionOption[] = [
            { id: 'A', text: String(optA).trim(), isCorrect: correctKey === 'A' },
            { id: 'B', text: String(optB).trim(), isCorrect: correctKey === 'B' },
            { id: 'C', text: String(optC).trim(), isCorrect: correctKey === 'C' },
            { id: 'D', text: String(optD).trim(), isCorrect: correctKey === 'D' }
          ];

          if (!options.some(o => o.isCorrect)) {
            options[0].isCorrect = true;
          }

          return {
            questionText: String(qText).trim(),
            questionMedia: String(qMedia).trim() || undefined,
            options,
            explanation: String(expl).trim(),
            difficulty: String(diff).trim()
          };
        }).filter(q => q.questionText && q.options.some(o => o.text));

        if (formattedList.length === 0) {
          throw new Error('Không đọc được câu hỏi hợp lệ nào từ file. Vui lòng tải file mẫu để xem cấu trúc.');
        }

        setParsedQuestions(formattedList);
      } catch (err: any) {
        setError(err.message || 'Lỗi khi đọc file Excel.');
        setParsedQuestions([]);
      } finally {
        setLoading(false);
      }
    };
    reader.onerror = () => {
      setError('Lỗi khi đọc tệp tin.');
      setLoading(false);
    };
    reader.readAsBinaryString(file);
  };

  const handleSaveBulk = async () => {
    if (parsedQuestions.length === 0 && !selectedRawFile) return;

    setSaving(true);
    setError(null);

    try {
      if (selectedRawFile) {
        // Gửi FormData lên POST /api/v1/questions/import-excel kèm field 'file' và 'setId'
        const formData = new FormData();
        formData.append('file', selectedRawFile);
        formData.append('setId', targetSet.id);

        const token = getStoredToken();
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const res = await customFetch('/api/v1/questions/import-excel', {
          method: 'POST',
          headers,
          credentials: 'include',
          body: formData
        });

        const data = await res.json().catch(() => ({}));
        
        if (res.status === 403 || data.isQuotaExceeded) {
          const errorMsg = data.message || 'Số lượng câu hỏi trong file vượt quá hạn mức tối đa cho phép của gói hiện tại.';
          if (onQuotaExceeded) {
            onClose();
            onQuotaExceeded(errorMsg);
            return;
          }
          throw new Error(errorMsg);
        }

        if (!res.ok || !data.success) {
          // Fallback sang POST /api/v1/questions/bulk-add nếu cần
          if (parsedQuestions.length > 0) {
            const fallbackRes = await customFetch('/api/v1/questions/bulk-add', {
              method: 'POST',
              headers: getAuthHeaders(),
              credentials: 'include',
              body: JSON.stringify({
                setId: targetSet.id,
                questions: parsedQuestions
              })
            });
            const fallbackData = await fallbackRes.json().catch(() => ({}));
            
            if (fallbackRes.status === 403 || fallbackData.isQuotaExceeded) {
              const errorMsg = fallbackData.message || 'Số lượng câu hỏi vượt quá hạn mức tối đa cho phép của gói hiện tại.';
              if (onQuotaExceeded) {
                onClose();
                onQuotaExceeded(errorMsg);
                return;
              }
              throw new Error(errorMsg);
            }

            if (!fallbackRes.ok || !fallbackData.success) {
              throw new Error(data.message || fallbackData.message || 'Không thể lưu câu hỏi.');
            }
            onSuccess(fallbackData.count || parsedQuestions.length);
            onClose();
            return;
          }
          throw new Error(data.message || 'Không thể import file Excel.');
        }

        onSuccess(data.count || parsedQuestions.length);
        onClose();
      } else {
        const res = await customFetch('/api/v1/questions/bulk-add', {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            setId: targetSet.id,
            questions: parsedQuestions
          })
        });

        const data = await res.json().catch(() => ({}));
        
        if (res.status === 403 || data.isQuotaExceeded) {
          const errorMsg = data.message || 'Số lượng câu hỏi vượt quá hạn mức tối đa cho phép của gói hiện tại.';
          if (onQuotaExceeded) {
            onClose();
            onQuotaExceeded(errorMsg);
            return;
          }
          throw new Error(errorMsg);
        }

        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Không thể lưu câu hỏi.');
        }

        onSuccess(data.count || parsedQuestions.length);
        onClose();
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Đã xảy ra lỗi hệ thống';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div 
        className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl border border-slate-200 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 p-1.5 rounded-xl hover:bg-slate-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center space-x-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
            <FileSpreadsheet className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              Nhập câu hỏi từ file Excel / CSV
            </h3>
            <p className="text-xs text-slate-500">
              Bộ: <span className="font-semibold text-slate-700">{targetSet.name}</span> ({targetSet.subject} - Lớp {targetSet.grade})
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3.5 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Download Sample & Upload Area */}
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-emerald-50/70 border border-emerald-200/80 rounded-xl text-xs">
            <div className="text-emerald-900">
              <span className="font-bold">Chưa có file mẫu?</span> Tải bảng mẫu chuẩn định dạng để điền câu hỏi nhanh nhất.
            </div>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="px-3 py-1.5 bg-white hover:bg-emerald-100 text-emerald-800 font-bold rounded-lg border border-emerald-300 shadow-2xs flex items-center space-x-1.5 transition-colors cursor-pointer shrink-0"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Tải file Excel mẫu</span>
            </button>
          </div>

          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-slate-300 hover:border-emerald-500 bg-slate-50/60 hover:bg-emerald-50/30 rounded-2xl p-6 text-center cursor-pointer transition-all flex flex-col items-center justify-center space-y-2"
          >
            <UploadCloud className="w-8 h-8 text-emerald-600" />
            <div>
              <p className="text-sm font-bold text-slate-700">
                {fileName ? fileName : 'Bấm để chọn file Excel (.xlsx, .xls) hoặc CSV'}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                Hệ thống tự động trích xuất các cột: Câu hỏi, Đáp án A, B, C, D, Đáp án đúng, Giải thích
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx, .xls, .csv"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>

          {/* Preview list if parsed */}
          {parsedQuestions.length > 0 && (
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between text-xs font-bold text-slate-700">
                <span>Đã phát hiện {parsedQuestions.length} câu hỏi hợp lệ:</span>
                <span className="text-emerald-700">Sẵn sàng nhập vào kho</span>
              </div>

              <div className="max-h-60 overflow-y-auto space-y-2 border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                {parsedQuestions.map((q, idx) => (
                  <div key={idx} className="bg-white p-2.5 rounded-lg border border-slate-200 text-xs space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-800">
                        {idx + 1}. {q.questionText}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 font-semibold">
                        {q.difficulty}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-[11px] text-slate-600">
                      {q.options.map((opt, optIdx) => {
                        const optKey = opt.key || (opt.id && opt.id.length === 1 ? opt.id : String.fromCharCode(65 + optIdx));
                        return (
                        <div key={opt.id || optIdx} className={opt.isCorrect ? 'text-emerald-700 font-bold' : ''}>
                          {optKey}: {opt.text} {opt.isCorrect && '✓'}
                        </div>
                      )})}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-end space-x-2.5 pt-4 border-t border-slate-100">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={saving}
            >
              Đóng
            </Button>
            <Button
              type="button"
              onClick={handleSaveBulk}
              disabled={saving || parsedQuestions.length === 0}
              className="bg-[#236539] hover:bg-[#1b4f2c] text-white shadow-md flex items-center space-x-1.5"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              <span>Nhập {parsedQuestions.length} câu hỏi</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
